/**
 * In-browser 6-axis IMU fusion for the session replay (ROADMAP
 * CAPTURE-IMU-RAW Phase 3).
 *
 * Raw (`fusionMode: 'off'`) recordings carry only `accelerometer` +
 * `gyroscope`, but the 3D hand-trajectory needs `quaternion` +
 * `linearAcceleration`. This synthesizes them in the browser — mirroring
 * the server pre-pass (`api/app/worker/imu_prepass.py`) and the on-device
 * `feedFusion` (`capture-app/src/ble/metawear.ts`): one Madgwick step per
 * accel paired with the most-recently-arrived gyro (array/file order, which
 * is arrival order), `dt` from the span-derived mean period — deviceTime
 * is the phone's receive time, not a board clock.
 *
 * The Madgwick kernel is a copy of `capture-app/src/ble/hostFusion.ts` (the
 * Python port lives in `api/app/worker/imu_fusion.py`). Keep the three in
 * lockstep — `imuFusion.test.ts` parity-gates this copy against the same
 * golden fixture the Python port uses. Units: accel g, gyro deg/s.
 */
import {
  applyBias,
  REST_WINDOW,
  updateBias,
  zeroBias,
  type GyroBias,
} from './gyroBias';
import type { SensorSample } from './types';

const DEG_TO_RAD = Math.PI / 180;
const ACCEL_GATE_LOW_G = 0.85;
const ACCEL_GATE_HIGH_G = 1.15;

/** Stateful 6-axis Madgwick filter for one IMU (port of hostFusion.ts). */
export class MadgwickIMU {
  private q0 = 1;
  private q1 = 0;
  private q2 = 0;
  private q3 = 0;
  private initialised = false;

  constructor(private readonly beta: number = 0.1) {}

  get isInitialised(): boolean {
    return this.initialised;
  }

  /** Seed orientation from the first stable accel reading (see hostFusion.ts). */
  initFromAccel(ax: number, ay: number, az: number): void {
    const m = Math.sqrt(ax * ax + ay * ay + az * az);
    if (m < 1e-6) return;
    const nx = ax / m;
    const ny = ay / m;
    const nz = az / m;
    if (nz > 0.9999) {
      this.q0 = 1;
      this.q1 = 0;
      this.q2 = 0;
      this.q3 = 0;
    } else if (nz < -0.9999) {
      this.q0 = 0;
      this.q1 = 1;
      this.q2 = 0;
      this.q3 = 0;
    } else {
      const cx = ny;
      const cy = -nx;
      const cMag = Math.sqrt(cx * cx + cy * cy);
      const half = Math.acos(nz) / 2;
      const s = Math.sin(half);
      this.q0 = Math.cos(half);
      this.q1 = (s * cx) / cMag;
      this.q2 = (s * cy) / cMag;
      this.q3 = 0;
    }
    this.initialised = true;
  }

  /** Step the filter with one accel + gyro pair (`dt` in s). Returns (w,x,y,z). */
  update(
    ax: number, ay: number, az: number,
    gxDeg: number, gyDeg: number, gzDeg: number,
    dt: number,
  ): [number, number, number, number] {
    const gx = gxDeg * DEG_TO_RAD;
    const gy = gyDeg * DEG_TO_RAD;
    const gz = gzDeg * DEG_TO_RAD;

    let q0 = this.q0;
    let q1 = this.q1;
    let q2 = this.q2;
    let q3 = this.q3;

    let qDot0 = 0.5 * (-q1 * gx - q2 * gy - q3 * gz);
    let qDot1 = 0.5 * (q0 * gx + q2 * gz - q3 * gy);
    let qDot2 = 0.5 * (q0 * gy - q1 * gz + q3 * gx);
    let qDot3 = 0.5 * (q0 * gz + q1 * gy - q2 * gx);

    const aMag = Math.sqrt(ax * ax + ay * ay + az * az);
    if (aMag >= ACCEL_GATE_LOW_G && aMag <= ACCEL_GATE_HIGH_G) {
      const axN = ax / aMag;
      const ayN = ay / aMag;
      const azN = az / aMag;

      const _2q0 = 2 * q0;
      const _2q1 = 2 * q1;
      const _2q2 = 2 * q2;
      const _2q3 = 2 * q3;
      const _4q0 = 4 * q0;
      const _4q1 = 4 * q1;
      const _4q2 = 4 * q2;
      const _8q1 = 8 * q1;
      const _8q2 = 8 * q2;
      const q0q0 = q0 * q0;
      const q1q1 = q1 * q1;
      const q2q2 = q2 * q2;
      const q3q3 = q3 * q3;

      let s0 = _4q0 * q2q2 + _2q2 * axN + _4q0 * q1q1 - _2q1 * ayN;
      let s1 = _4q1 * q3q3 - _2q3 * axN + 4 * q0q0 * q1 - _2q0 * ayN
             - _4q1 + _8q1 * q1q1 + _8q1 * q2q2 + _4q1 * azN;
      let s2 = 4 * q0q0 * q2 + _2q0 * axN + _4q2 * q3q3 - _2q3 * ayN
             - _4q2 + _8q2 * q1q1 + _8q2 * q2q2 + _4q2 * azN;
      let s3 = 4 * q1q1 * q3 - _2q1 * axN + 4 * q2q2 * q3 - _2q2 * ayN;
      const sMag = Math.sqrt(s0 * s0 + s1 * s1 + s2 * s2 + s3 * s3);
      if (sMag > 1e-6) {
        s0 /= sMag;
        s1 /= sMag;
        s2 /= sMag;
        s3 /= sMag;
        qDot0 -= this.beta * s0;
        qDot1 -= this.beta * s1;
        qDot2 -= this.beta * s2;
        qDot3 -= this.beta * s3;
      }
    }

    q0 += qDot0 * dt;
    q1 += qDot1 * dt;
    q2 += qDot2 * dt;
    q3 += qDot3 * dt;
    const qMag = Math.sqrt(q0 * q0 + q1 * q1 + q2 * q2 + q3 * q3);
    if (qMag > 0) {
      q0 /= qMag;
      q1 /= qMag;
      q2 /= qMag;
      q3 /= qMag;
    }
    this.q0 = q0;
    this.q1 = q1;
    this.q2 = q2;
    this.q3 = q3;
    return [q0, q1, q2, q3];
  }
}

/** Remove gravity from a body-frame accel measurement (in g → in g). */
export function removeGravity(
  qw: number, qx: number, qy: number, qz: number,
  ax: number, ay: number, az: number,
): [number, number, number] {
  const gx = 2 * (qx * qz - qw * qy);
  const gy = 2 * (qw * qx + qy * qz);
  const gz = 1 - 2 * (qx * qx + qy * qy);
  return [ax - gx, ay - gy, az - gz];
}

const IMU_SENSORS = ['metawear-left', 'metawear-right'] as const;
const DEFAULT_DT_S = 0.01;

function nominalDt(deviceTimes: number[]): number {
  if (deviceTimes.length >= 2) {
    const span = (deviceTimes[deviceTimes.length - 1] - deviceTimes[0]) / 1000;
    if (span > 0) return span / (deviceTimes.length - 1);
  }
  return DEFAULT_DT_S;
}

/**
 * For a raw recording, return `samples` plus synthesized `quaternion` +
 * `linearAcceleration` samples (tagged with each accel's hostTime, so
 * `computeTrajectories`'s host-time pairing lines them up). For an
 * already-fused recording (host/legacy) or a non-IMU one, returns
 * `samples` unchanged — dual-support, mirroring the server pre-pass.
 */
export function ensureFusionStreams(samples: SensorSample[]): SensorSample[] {
  const isImu = (s: SensorSample) => s.sensorId.startsWith('metawear-');
  const alreadyFused = samples.some(
    (s) => isImu(s) && (s.stream === 'quaternion' || s.stream === 'linearAcceleration'),
  );
  if (alreadyFused) return samples;
  const hasAccel = samples.some((s) => isImu(s) && s.stream === 'accelerometer');
  const hasGyro = samples.some((s) => isImu(s) && s.stream === 'gyroscope');
  if (!(hasAccel && hasGyro)) return samples;

  /** Rolling accel arrivals per hand — MUST match FUSION_DT_WINDOW in
   *  capture-app/src/ble/metawear.ts and _FUSION_DT_WINDOW in
   *  imu_prepass.py. The length changes the answer. */
  const FUSION_DT_WINDOW = 100;
  const arrivals: Record<string, number[]> = {
    'metawear-left': [],
    'metawear-right': [],
  };
  /** Gyro offsets learned from rest, per hand — mirrors feedFusion and
   *  imu_prepass.py. Applied to the fusion input only; the raw gyroscope
   *  stream it is derived from is untouched. */
  const bias: Record<string, GyroBias> = {
    'metawear-left': zeroBias(),
    'metawear-right': zeroBias(),
  };
  const biasAccel: Record<string, number[][]> = {
    'metawear-left': [],
    'metawear-right': [],
  };
  const biasGyro: Record<string, number[][]> = {
    'metawear-left': [],
    'metawear-right': [],
  };
  const nominal: Record<string, number> = {};
  const filt: Record<string, MadgwickIMU> = {};
  const lastGyro: Record<string, number[] | null> = {};
  for (const hand of IMU_SENSORS) {
    nominal[hand] = nominalDt(
      samples
        .filter((s) => s.sensorId === hand && s.stream === 'accelerometer' && s.deviceTime != null)
        .map((s) => s.deviceTime as number),
    );
    filt[hand] = new MadgwickIMU();
    lastGyro[hand] = null;
  }

  const synth: SensorSample[] = [];
  // Walk in array (file/arrival) order — the pairing the device used.
  for (const s of samples) {
    if (s.sensorId !== 'metawear-left' && s.sensorId !== 'metawear-right') continue;
    if (s.stream === 'gyroscope') {
      lastGyro[s.sensorId] = s.values;
      continue;
    }
    if (s.stream !== 'accelerometer') continue;
    const g = lastGyro[s.sensorId];
    if (!g || s.deviceTime == null || s.values.length < 3) continue;
    const [ax, ay, az] = s.values;
    const f = filt[s.sensorId];
    if (!f.isInitialised) f.initFromAccel(ax, ay, az);
    // Span-derived mean period, ALWAYS — never the gap to the previous
    // sample. `deviceTime` is not a device clock: the MetaWear SDK stamps
    // the phone's RECEIVE time, and packed mode gives every sample in a
    // notification the same stamp. On a real recording 67% of consecutive
    // gaps are exactly 0 ms and the rest run 34-82 ms, against a true
    // 10 ms period — so the old override stepped the filter 3-8x too far
    // at every burst boundary, inflating rotation by ~10%.
    // Mirrors imu_prepass.py and feedFusion; the L2 parity test pins them
    // together. The window length is load-bearing — a whole-recording mean
    // is a defensible average too, and moved bimanual_dexterity by 17%.
    const win = arrivals[s.sensorId];
    win.push(s.hostTime);
    if (win.length > FUSION_DT_WINDOW) win.shift();
    let dt = nominal[s.sensorId];
    if (win.length >= 2) {
      const mean = ((win[win.length - 1] - win[0]) / 1000) / (win.length - 1);
      if (mean > 0 && mean < 0.2) dt = mean;
    }
    biasAccel[s.sensorId].push([ax, ay, az]);
    biasGyro[s.sensorId].push([g[0], g[1], g[2]]);
    if (biasAccel[s.sensorId].length >= REST_WINDOW) {
      bias[s.sensorId] = updateBias(
        bias[s.sensorId],
        biasAccel[s.sensorId],
        biasGyro[s.sensorId],
      );
      biasAccel[s.sensorId] = [];
      biasGyro[s.sensorId] = [];
    }
    const [gx, gy, gz] = applyBias(bias[s.sensorId], g[0], g[1], g[2]);
    const [qw, qx, qy, qz] = f.update(ax, ay, az, gx, gy, gz, dt);
    const [lx, ly, lz] = removeGravity(qw, qx, qy, qz, ax, ay, az);
    synth.push({
      sensorId: s.sensorId, stream: 'quaternion',
      hostTime: s.hostTime, deviceTime: s.deviceTime,
      values: [qw, qx, qy, qz], phase: s.phase,
    });
    synth.push({
      sensorId: s.sensorId, stream: 'linearAcceleration',
      hostTime: s.hostTime, deviceTime: s.deviceTime,
      values: [lx, ly, lz], phase: s.phase,
    });
  }
  return synth.length > 0 ? samples.concat(synth) : samples;
}
